// fallow-ignore-next-line boundary-violation -- shared sales helper lives in workspace zone; move pending
import { createCancelable } from "app/pages/workspace/sales/helpers";
import type { IFetchService } from "app/services/fetchService";

import type {
    ExportSettingResponse,
    AddExportSettingsRequest,
    SaveExportSettingsResponse,
    ExportType,
} from "../../type";
import type { DynamicsPreDefinedType } from "../../types/shared";

export type DomainExportResult = {
    processedRecords: number;
    exportHistoryUrl?: string;
};

export type DynamicsExportSettingsServiceType = {
    getDynamicsExportSettings(type: ExportType): Promise<ExportSettingResponse[]>;
    fetchDynamicsExportSettings(cancellation: AbortSignal): Promise<ExportSettingResponse[]>;
    saveDynamicsExportSettings(
        settings: AddExportSettingsRequest,
        type: DynamicsPreDefinedType,
    ): Promise<SaveExportSettingsResponse>;
    exportDomainsTo(
        domains: readonly string[],
        countries: readonly number[],
        leadsType: string,
        listId?: string,
    ): Promise<DomainExportResult>;
    exportDomainsAsLeadTo(
        domains: readonly string[],
        countries: readonly number[],
        leadsType: string,
    ): Promise<DomainExportResult>;
};

export const createDynamicsExportSettingsApiService = (
    fetchService: IFetchService,
): DynamicsExportSettingsServiceType => ({
    getDynamicsExportSettings: createCancelable(
        (signal: AbortSignal, type: ExportType): Promise<ExportSettingResponse[]> => {
            return fetchService.get(`sales-api/microsoft-dynamics/mapping-field?type=${type}`, undefined, {
                cancellation: signal,
            });
        },
    ),
    fetchDynamicsExportSettings(cancellation: AbortSignal) {
        return fetchService.get<ExportSettingResponse[]>("sales-api/microsoft-dynamics/mapping-field", undefined, {
            cancellation,
        });
    },
    saveDynamicsExportSettings(
        settings: AddExportSettingsRequest,
        type: DynamicsPreDefinedType,
    ): Promise<SaveExportSettingsResponse> {
        return fetchService.post(`sales-api/microsoft-dynamics/mapping-field/export/${type}`, settings);
    },
    exportDomainsTo(
        domains: readonly string[],
        countries: readonly number[],
        leadsType: string,
        listId?: string,
    ): Promise<DomainExportResult> {
        const path =
            leadsType === "companies"
                ? "sales-api/microsoft-dynamics/export/companies/accounts"
                : "sales-api/microsoft-dynamics/export/accounts";
        return fetchService.post(path, { domains, countries, listId });
    },
    exportDomainsAsLeadTo(
        domains: readonly string[],
        countries: readonly number[],
        leadsType: string,
    ): Promise<DomainExportResult> {
        const path =
            leadsType === "companies"
                ? "sales-api/microsoft-dynamics/export/companies/company-as-lead"
                : "sales-api/microsoft-dynamics/export/company-as-lead";
        return fetchService.post(path, { domains, countries });
    },
});
